import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { StatementsService } from '../../services/statements.service';
import {
  Statement,
  StatementSearchRequest,
  StatementExportRequest,
  Statements,
  InterventionType,
  TowingTypes,
} from '../../models/statements.model';
import { TablePageEvent } from 'primeng/table';
import { Subject, debounceTime, Subscription } from 'rxjs';

/**
 * Composant pour la gestion et l'affichage des déclarations
 * Permet de rechercher, filtrer, paginer et exporter les déclarations
 */
@Component({
  standalone: false,
  selector: 'app-statements',
  templateUrl: './statements.component.html',
  styleUrl: './statements.component.scss',
})
export class StatementsComponent implements OnInit, OnDestroy {
  // Injection des services nécessaires
  private readonly statementsService: StatementsService =
    inject(StatementsService);
  private readonly router: Router = inject(Router);

  statements: Statement[] = [];
  loading: boolean = true;
  skeletonRows = Array(5).fill(0);

  // Propriétés de pagination
  totalRecords: number = 0;
  rows: number = 10;
  first: number = 0;

  // Propriété pour la validation des dates
  minEndDate: Date | null = null;

  // Propriétés de filtrage
  filters: StatementSearchRequest = {
    plate: null,
    startDate: null,
    endDate: null,
    brand: null,
    interventiontypeid: null,
    towingtypeid: null,
    isOnlyMovement: false,
    isTowingDone: false,
    pageNumber: 1,
    pageSize: 10,
  };
  interventionTypes: InterventionType[] = [];
  towingTypes: TowingTypes[] = [];

  // Clé pour le stockage des filtres dans le sessionStorage
  private readonly FILTERS_STORAGE_KEY = 'statements-filters';

  // Subjects pour la gestion du debounce et subscription pour le cleanup
  private readonly searchSubject$ = new Subject<void>();
  private searchSubscription?: Subscription;
  /**
   * Initialisation du composant
   * Charge les types d'intervention, de remorquage et les déclarations
   */
  ngOnInit() {
    this.initializeSearchDebounce();
    this.loadStoredFilters();
    this.loadInterventionTypes();
    this.loadTowingTypes();
    const initialPage = this.filters.pageNumber || 1;
    this.loadStatements(initialPage);
  }

  /**
   * Nettoyage lors de la destruction du composant
   */
  ngOnDestroy() {
    // Désinscription manuelle de la subscription de debounce
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  /**
   * Initialise le système de debounce pour les recherches
   */
  private initializeSearchDebounce() {
    this.searchSubscription = this.searchSubject$
      .pipe(
        debounceTime(500) // Attend 500ms après la dernière émission
      )
      .subscribe(() => {
        this.first = 0;
        this.loadStatements(1);
      });
  }

  /**
   * Charge la liste des types d'intervention depuis le service
   */
  loadInterventionTypes() {
    this.statementsService.getInterventionTypes().subscribe({
      next: (types) => {
        this.interventionTypes = types;
      },
      error: (error) => {
        console.error('Error loading intervention types:', error);
      },
    });
  }

  /**
   * Charge la liste des types de remorquage depuis le service
   */
  loadTowingTypes() {
    this.statementsService.getTowingTypes().subscribe({
      next: (types) => {
        this.towingTypes = types;
      },
      error: (error) => {
        console.error('Error loading towing types:', error);
      },
    });
  }

  /**
   * Charge les déclarations avec les filtres et la pagination appliqués
   * @param page Numéro de la page à charger (par défaut 1)
   */
  loadStatements(page: number = 1) {
    this.loading = true;
    this.filters.pageNumber = page;
    this.filters.pageSize = this.rows;
    this.skeletonRows = Array(this.rows).fill(0);

    // Nettoyage des chaînes vides avant l'envoi
    const cleanedFilters: StatementSearchRequest = {
      ...this.filters,
      plate: this.filters.plate?.trim() ?? null,
      brand: this.filters.brand?.trim() ?? null,
      interventiontypeid: this.filters.interventiontypeid ?? null,
      towingtypeid: this.filters.towingtypeid ?? null,
      startDate: this.filters.startDate ?? null,
      endDate: this.filters.endDate ?? null,
    };
    this.statementsService.searchStatements(cleanedFilters).subscribe({
      next: (data: Statements) => {
        this.statements = data.statements;
        this.totalRecords = data.totalCount;
        // Calculer le first en fonction de la page actuelle
        this.first = (page - 1) * this.rows;
        // Sauvegarder l'état actuel après le chargement réussi
        this.saveFilters();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading statements:', error);
        this.loading = false;
      },
    });
  }
  /**
   * Gère le changement de la date de début
   * Met à jour la date minimale pour la date de fin et valide la cohérence des dates
   * @param event Nouvelle date de début sélectionnée
   */
  onStartDateChange(event: Date) {
    if (event) {
      // Si la date de début est changée, on met à jour la date minimale pour la date de fin
      this.minEndDate = new Date(event);

      // Si la date de fin est inférieure à la nouvelle date de début, on la réinitialise
      if (this.filters.endDate && new Date(this.filters.endDate) <= event) {
        this.filters.endDate = null;
      }
    } else {
      this.minEndDate = null;
    }
    this.onFilterChangeImmediate();
  }
  /**
   * Gère le changement de page dans le tableau
   * @param event Événement de changement de page contenant les informations de pagination
   */
  onPageChange(event: TablePageEvent) {
    const page = Math.floor(event.first / event.rows) + 1;
    this.rows = event.rows;
    this.first = event.first;

    this.saveFilters();
    this.loadStatements(page);
  }
  /**
   * Applique les filtres et recharge les données à partir de la première page
   */
  onFilter() {
    this.saveFilters();
    this.first = 0;
    this.loadStatements(1);
  }

  /**
   * Gère les changements de filtres avec debounce (pour les champs de texte)
   */
  onFilterChangeWithDebounce() {
    this.saveFilters();
    this.searchSubject$.next();
  }

  /**
   * Gère les changements de filtres sans debounce (pour les dropdowns, dates, etc.)
   */
  onFilterChangeImmediate() {
    this.saveFilters();
    this.first = 0;
    this.loadStatements(1);
  }

  /**
   * Gère les changements de filtres
   * Sauvegarde automatiquement les filtres et applique le filtre
   */
  onFilterChange() {
    this.saveFilters();
    this.onFilter();
  }

  /**
   * Remet à zéro tous les filtres et recharge les données
   */
  clearFilters() {
    this.filters = {
      plate: null,
      startDate: null,
      endDate: null,
      brand: null,
      interventiontypeid: null,
      towingtypeid: null,
      isOnlyMovement: false,
      isTowingDone: false,
      pageNumber: 1,
      pageSize: this.rows,
    };
    this.minEndDate = null;
    // Supprimer les filtres sauvegardés lors de la remise à zéro
    sessionStorage.removeItem(this.FILTERS_STORAGE_KEY);
    this.loadStatements(1);
  }

  /**
   * Actualise les données en gardant les filtres actuels
   */
  refreshStatements() {
    const currentPage = Math.floor(this.first / this.rows) + 1;
    this.loadStatements(currentPage);
  }

  /**
   * Navigue vers la page de détails d'une déclaration
   * @param statement La déclaration dont on veut voir les détails
   */
  viewDetails(statement: Statement) {
    this.router.navigate(['home', statement.id]);
  }

  /**
   * Vérifie si le pays est la Belgique
   * @param country Le nom du pays à vérifier
   * @returns true si le pays commence par "bel" (case insensitive)
   */
  isCountryBelgium(country: string): boolean {
    return country?.toLowerCase().startsWith('bel') ?? false;
  }

  /**
   * Exporte les donées des filtres vers un fichier Excel
   * Génère et télécharge automatiquement le fichier
   */
  exportToExcel() {
    const exportRequest: StatementExportRequest = {
      plate: this.filters.plate ?? '',
      startDate: this.filters.startDate ?? '',
      endDate: this.filters.endDate ?? '',
      interventionType: this.filters.interventiontypeid ?? '',
      towingType: this.filters.towingtypeid ?? '',
      isOnlyMovement: this.filters.isOnlyMovement,
      isTowingDone: this.filters.isTowingDone,
    };

    this.statementsService.exportToExcel(exportRequest).subscribe({
      next: (blob: Blob) => {
        // Créer une URL d'objet pour le blob
        const url = window.URL.createObjectURL(blob);

        // Créer un élément pour le téléchargement
        const a = document.createElement('a');
        a.href = url;
        a.download = `statements-export-${
          new Date().toISOString().split('T')[0]
        }.xlsx`;

        // Ajouter au DOM, déclencher le téléchargement et nettoyer
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      },
      error: (error) => {
        console.error('Error exporting to Excel:', error);
      },
    });
  }
  /**
   * Charge les filtres stockés depuis le sessionStorage
   * Applique les filtres et la pagination s'ils existent
   */
  private loadStoredFilters() {
    try {
      const storedFilters = sessionStorage.getItem(this.FILTERS_STORAGE_KEY);
      if (storedFilters) {
        const parsedFilters = JSON.parse(storedFilters);

        // Reconstituer les dates à partir des chaînes ISO
        if (parsedFilters.startDate) {
          parsedFilters.startDate = new Date(parsedFilters.startDate);
          this.minEndDate = new Date(parsedFilters.startDate);
        }
        if (parsedFilters.endDate) {
          parsedFilters.endDate = new Date(parsedFilters.endDate);
        }

        // Restaurer les propriétés de pagination
        if (parsedFilters.rows) {
          this.rows = parsedFilters.rows;
        }
        if (parsedFilters.first !== undefined) {
          this.first = parsedFilters.first;
        }

        // Appliquer les filtres chargés
        this.filters = { ...this.filters, ...parsedFilters };
      }
    } catch (error) {
      console.warn(
        'Erreur lors du chargement des filtres depuis le sessionStorage:',
        error
      );
      // En cas d'erreur, on supprime les données corrompues
      sessionStorage.removeItem(this.FILTERS_STORAGE_KEY);
    }
  }
  /**
   * Sauvegarde les filtres actuels dans le sessionStorage
   * Inclut les propriétés de pagination et de filtrage
   */
  private saveFilters() {
    try {
      const filtersToSave = {
        plate: this.filters.plate,
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        brand: this.filters.brand,
        interventiontypeid: this.filters.interventiontypeid,
        towingtypeid: this.filters.towingtypeid,
        isOnlyMovement: this.filters.isOnlyMovement,
        isTowingDone: this.filters.isTowingDone,
        pageNumber: this.filters.pageNumber,
        pageSize: this.filters.pageSize,
        rows: this.rows,
        first: this.first,
      };
      sessionStorage.setItem(
        this.FILTERS_STORAGE_KEY,
        JSON.stringify(filtersToSave)
      );
    } catch (error) {
      console.warn(
        'Erreur lors de la sauvegarde des filtres dans le sessionStorage:',
        error
      );
    }
  }
}
